import { useState } from "react";
import { useHistory } from "react-router-dom";
import MainLayout from "Layouts/MainLayout/MainLayout";
import Table from "Components/Common/Table/Table";
import { Button } from "Components/Common/Button/Button";
import { InputField } from "Components/Common/InputField/InputField";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { PaginationBtns } from "Components/Common/PaginationBtns/PaginationBtns";
import ClassNames from "Helpers/Common";
import "Colors.css";
import {
  useEmailMarketingConfig,
  useAudienceCount,
  useCampaigns,
  useCreateCampaign,
  useStartCampaign,
  useDeleteCampaign,
} from "CustomHooks/useEmailMarketing";

const PER_PAGE = 10;
const TH = "px-4 py-3 text-left text-xs font-medium light_grayish_navy uppercase tracking-wider";
const TD = "px-4 py-3 text-sm text-black whitespace-nowrap";

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-700",
  running: "bg-green-100 text-green-700",
  completed: "bg-blue-100 text-blue-700",
  cancelled: "bg-red-100 text-red-700",
};

export const Badge = ({ status }) => (
  <span className={ClassNames("px-2 py-0.5 rounded text-xs font-semibold capitalize", STATUS_COLORS[status] || "bg-gray-100 text-gray-700")}>
    {status}
  </span>
);

const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="font-semibold sapphire text-lg">{title}</div>
          <button type="button" onClick={onClose} className="light_grayish_navy text-2xl leading-none">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

const IsolationBanner = ({ config }) => {
  if (!config) return null;
  const ready = config.apiKeyConfigured && !!config.unsubscribeGroupId;
  const Row = ({ label, value, warn }) => (
    <div>
      <span className="light_grayish_navy">{label}: </span>
      <span className={ClassNames("font-semibold", warn ? "text-red-600" : "text-black")}>{value}</span>
    </div>
  );
  return (
    <div className={ClassNames("rounded-md border px-4 py-3 text-sm mb-4", ready ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50")}>
      <div className="font-semibold sapphire mb-2">Marketing stream</div>
      <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
        <Row label="Domain" value={config.domain} />
        <Row label="From" value={config.fromEmail} />
        <Row label="API key" value={config.apiKeyConfigured ? "configured" : "MISSING"} warn={!config.apiKeyConfigured} />
        <Row label="IP pool" value={config.ipPool || "shared"} />
        <Row label="Unsubscribe group" value={config.unsubscribeGroupId || "MISSING"} warn={!config.unsubscribeGroupId} />
        <Row label="Max batch" value={config.maxBatchSize} />
      </div>
      <div className="text-xs light_grayish_navy mt-2">
        Transactional/clinical email is a separate stream and is never affected by this campaign.
      </div>
    </div>
  );
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const CreateCampaignModal = ({ open, onClose, config }) => {
  const empty = { name: "", templateId: "", subject: "", audienceWindowDays: "90", audienceType: "patients", manualEmails: "" };
  const [form, setForm] = useState(empty);
  const [formKey, setFormKey] = useState(0);
  const create = useCreateCampaign();
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const isManual = form.audienceType === "manual";
  const windowDays = Number(form.audienceWindowDays) || 90;
  const audience = useAudienceCount(open && !isManual ? windowDays : null);

  // Client-side mirror of the backend normalizer: split / trim / lowercase / validate / dedupe.
  const manualEmails = Array.from(new Set(
    form.manualEmails.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter((e) => EMAIL_RE.test(e))
  ));

  const canSubmit =
    form.name.trim() && form.templateId.trim() &&
    (isManual ? manualEmails.length > 0 : !audience.isFetching);

  const submit = () => {
    create.mutate(
      {
        name: form.name,
        templateId: form.templateId,
        subject: form.subject || undefined,
        audienceType: form.audienceType,
        ...(isManual ? { manualEmails } : { audienceWindowDays: windowDays }),
      },
      { onSuccess: () => { setForm(empty); setFormKey((k) => k + 1); onClose(); } }
    );
  };

  const TabBtn = ({ value, label }) => (
    <button
      type="button"
      onClick={() => set("audienceType")(value)}
      className={ClassNames(
        "px-3 py-1.5 text-sm rounded-md border transition-colors",
        form.audienceType === value ? "bg-blue-chill text-white border-blue-chill" : "bg-white light_grayish_navy border-gray-300 hover:bg-gray-50"
      )}
    >
      {label}
    </button>
  );

  return (
    <Modal open={open} onClose={onClose} title="New campaign">
      <IsolationBanner config={config} />
      <div key={formKey} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField label="Campaign name" placeholder="Weekly promo" value={form.name} onChange={set("name")} isRequired />
        <InputField label="SendGrid template id" placeholder="d-xxxxxxxx" value={form.templateId} onChange={set("templateId")} isRequired />
        <InputField label="Subject (optional)" placeholder="Subject line" value={form.subject} onChange={set("subject")} />
      </div>

      <div className="mt-4">
        <div className="text-xs font-medium light_grayish_navy uppercase tracking-wider mb-2">Audience source</div>
        <div className="flex gap-2 mb-3">
          <TabBtn value="patients" label="Patients (time window)" />
          <TabBtn value="manual" label="Manual emails" />
        </div>

        {isManual ? (
          <>
            <textarea
              rows={4}
              placeholder="Enter emails separated by comma, space, or new line"
              value={form.manualEmails}
              onChange={(e) => set("manualEmails")(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-chill"
            />
            <div className="mt-2 rounded-md bg-gray-50 border border-gray-200 px-4 py-2 text-sm">
              <span className="light_grayish_navy">Audience: </span>
              <span className="font-semibold text-black">{manualEmails.length} valid email{manualEmails.length === 1 ? "" : "s"}</span>
              <span className="light_grayish_navy"> — sends to exactly these addresses.</span>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Audience window (days)" type="number" placeholder="90" value={form.audienceWindowDays} onChange={set("audienceWindowDays")} />
            </div>
            <div className="mt-2 rounded-md bg-gray-50 border border-gray-200 px-4 py-2 text-sm">
              <span className="light_grayish_navy">Audience: </span>
              <span className="font-semibold text-black inline-flex items-center gap-1.5 align-middle">
                {audience.isFetching ? (
                  <>
                    <span className="w-3.5 h-3.5 border-l-2 border-b-2 border-current rounded-full animate-spin inline-block" />
                    counting…
                  </>
                ) : (
                  `${audience.data?.count ?? 0} patients`
                )}
              </span>
              <span className="light_grayish_navy"> with a paid, non-refunded visit in the last {windowDays} days.</span>
            </div>
          </>
        )}
      </div>

      <div className="mt-5 flex items-center justify-end gap-3">
        {create.isError && (
          <span className="text-red-600 text-xs mr-auto">
            {create.error?.response?.data?.errors?.[0] || "Failed to create"}
          </span>
        )}
        <Button btnText="Cancel" variant="Secondary" onClick={onClose} className="py-2 px-4" />
        {/* Disabled until required fields are set + (patients) the count is done / (manual) at least one valid email. */}
        <Button btnText="Create campaign" onClick={submit} isLoading={create.isLoading} isDisabled={!canSubmit || create.isLoading} className="py-2 px-4" />
      </div>
    </Modal>
  );
};

const CampaignRow = ({ c }) => {
  const history = useHistory();
  const start = useStartCampaign();
  const del = useDeleteCampaign();

  const onDelete = () => {
    if (window.confirm(`Delete campaign "${c.name}"? This removes its recipient list too.`)) {
      del.mutate(c.id);
    }
  };

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => history.push(`/emails/${c.id}`)}>
      <td className={TD}>
        <div className="font-medium">{c.name}</div>
        <div className="text-xs light_grayish_navy">{c.templateId}</div>
      </td>
      <td className={TD}><Badge status={c.status} /></td>
      <td className={ClassNames(TD, "light_grayish_navy")}>{c.audienceWindowDays}d</td>
      <td className={TD}>{c.totalRecipients ?? 0}</td>
      <td className={TD}>{c.sentCount ?? 0}</td>
      <td className={ClassNames(TD, "text-green-700 font-medium")}>{c.deliveredCount ?? 0}</td>
      {/* stopPropagation so action clicks don't trigger the row's navigate */}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap gap-2">
          {/* Draft-only actions — a started/completed campaign can't be started or deleted. */}
          {c.status === "draft" && (
            <>
              <Button btnText="Start" onClick={() => start.mutate(c.id)} isLoading={start.isLoading} className="py-1 px-3 text-xs" />
              <Button btnText="Delete" variant="Delete" onClick={onDelete} isLoading={del.isLoading} className="py-1 px-3 text-xs" />
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export const EmailsPage = () => {
  const { data: config } = useEmailMarketingConfig();
  const { data: campaigns, isLoading } = useCampaigns();
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);

  const list = campaigns || [];
  const totalPages = Math.max(1, Math.ceil(list.length / PER_PAGE));
  const pageRows = list.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <MainLayout>
      <div className="p-8 flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <span className="font-semibold text-2xl sapphire">Emails</span>
          <Button btnText="Create Campaign" onClick={() => setShowCreate(true)} className="py-2 px-4" />
        </div>

        <Table
          heading={
            <>
              <th className={TH}>Campaign</th>
              <th className={TH}>Status</th>
              <th className={TH}>Window</th>
              <th className={TH}>Total</th>
              <th className={TH} title="Accepted by SendGrid (handed off — not yet delivered)">Accepted</th>
              <th className={TH} title="Delivered — receiving mail server accepted it">Delivered</th>
              <th className={TH}>Actions</th>
            </>
          }
          body={
            isLoading ? null : list.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center light_grayish_navy">No campaigns yet.</td></tr>
            ) : (
              pageRows.map((c) => <CampaignRow key={c.id} c={c} />)
            )
          }
        />
        {isLoading && <Spinner />}

        {list.length > 0 && (
          <div className="flex items-center justify-between">
            <div className="text-sm light_grayish_navy">
              Page {Math.min(page, totalPages)} of {totalPages} · {list.length} campaign{list.length === 1 ? "" : "s"}
            </div>
            <div className="w-56">
              <PaginationBtns
                onPrevious={() => page > 1 && setPage(page - 1)}
                onNext={() => page < totalPages && setPage(page + 1)}
                isPrevDisabled={page <= 1}
                isNextDisabled={page >= totalPages}
              />
            </div>
          </div>
        )}
      </div>

      <CreateCampaignModal open={showCreate} onClose={() => setShowCreate(false)} config={config} />
    </MainLayout>
  );
};

export default EmailsPage;
